/**
 * Isometric City Animation (等距城市动画)
 * 
 * 技术要点：
 * 1. 等距投影（Isometric Projection）- 30度角度 + 透视模拟
 * 2. 程序化生成城市建筑（随机高度、宽度、颜色）
 * 3. 3D 层级渲染（深度排序）
 * 4. 动态车辆和行人流动
 * 5. 昼夜变化效果（渐变背景）
 * 6. Spring 物理进入动画
 * 
 * 实现难度：中等
 * 视觉效果：现代、科技感、立体感
 * 适用场景：品牌展示、科技视频、城市可视化、产品介绍
 */

import React from 'react';
import { useCurrentFrame, useVideoConfig, interpolate, spring, Easing } from 'remotion';

// 等距投影坐标转换
const toIsometric = (x: number, y: number, z: number) => {
  const angle = Math.PI / 6; // 30度
  const isoX = (x - y) * Math.cos(angle);
  const isoY = (x + y) * Math.sin(angle) - z;
  return { x: isoX, y: isoY };
};

// 建筑物类型
interface Building {
  x: number;
  y: number;
  width: number;
  depth: number;
  height: number;
  color: string;
  delay: number;
}

// 车辆类型
interface Vehicle {
  startX: number;
  startY: number;
  endX: number;
  endY: number;
  color: string;
  speed: number;
}

// 行人类型
interface Pedestrian {
  x: number;
  y: number;
  color: string;
  speed: number;
}

export const IsometricCityAnimation: React.FC = () => {
  const frame = useCurrentFrame();
  const { width, height, fps } = useVideoConfig();

  // Spring 进入动画
  const entranceProgress = spring({
    frame,
    fps,
    config: { damping: 200, stiffness: 100 },
  });

  // 背景渐变（昼夜变化）
  const bgProgress = (frame / fps) % 6; // 6秒循环
  const isNight = bgProgress > 3;
  const bgHue = interpolate(bgProgress, [0, 3, 6], [200, 240, 200]);
  const bgLightness = isNight ? 15 : 35;

  // 生成建筑物（程序化）
  const buildings: Building[] = [];
  const gridSize = 5;
  const spacing = 100;

  for (let i = 0; i < gridSize; i++) {
    for (let j = 0; j < gridSize; j++) {
      // 跳过中心区域（广场）
      if (i === 2 && j === 2) continue;

      const seed = i * gridSize + j;
      const buildingHeight = 80 + Math.sin(seed) * 120;
      const buildingWidth = 40 + Math.cos(seed) * 20;
      const buildingDepth = 40 + Math.sin(seed * 2) * 20;

      // 颜色：暖色系（橙/黄/红）
      const hue = 20 + seed * 8;
      const saturation = 60 + seed * 3;
      const lightness = 45 + seed * 2;

      buildings.push({
        x: (i - gridSize / 2) * spacing,
        y: (j - gridSize / 2) * spacing,
        width: buildingWidth,
        depth: buildingDepth,
        height: buildingHeight,
        color: `hsl(${hue}, ${saturation}%, ${lightness}%)`,
        delay: seed * 2,
      });
    }
  }

  // 生成车辆
  const vehicles: Vehicle[] = [
    { startX: -300, startY: 0, endX: 300, endY: 0, color: '#FF6B6B', speed: 2 },
    { startX: 0, startY: -300, endX: 0, endY: 300, color: '#4ECDC4', speed: 2.5 },
    { startX: -200, startY: -200, endX: 200, endY: 200, color: '#FFE66D', speed: 1.8 },
    { startX: 200, startY: -200, endX: -200, endY: 200, color: '#95E1D3', speed: 2.2 },
  ];

  // 生成行人
  const pedestrians: Pedestrian[] = Array.from({ length: 8 }, (_, i) => ({
    x: Math.sin(i * 2) * 150,
    y: Math.cos(i * 2) * 150,
    color: `hsl(${i * 45}, 70%, 60%)`,
    speed: 0.5 + i * 0.1,
  }));

  // 中心广场
  const centerPlaza = toIsometric(0, 0, 0);

  // 渲染建筑物
  const renderBuilding = (building: Building, index: number) => {
    const delay = building.delay;
    const buildingProgress = spring({
      frame: frame - delay,
      fps,
      config: { damping: 200, stiffness: 100 },
    });

    if (buildingProgress < 0.01) return null;

    const scale = interpolate(buildingProgress, [0, 1], [0, 1]);
    const opacity = interpolate(buildingProgress, [0, 1], [0, 1]);

    const { x: isoX, y: isoY } = toIsometric(building.x, building.y, 0);
    const halfWidth = building.width / 2;
    const halfDepth = building.depth / 2;

    // 等距投影的 4 个顶点
    const top = toIsometric(0, 0, building.height * scale);
    const frontLeft = toIsometric(-halfWidth, halfDepth, 0);
    const frontRight = toIsometric(halfWidth, halfDepth, 0);
    const backRight = toIsometric(halfWidth, -halfDepth, 0);
    const backLeft = toIsometric(-halfWidth, -halfDepth, 0);

    // 底部 4 个顶点
    const bottomFrontLeft = toIsometric(-halfWidth, halfDepth, 0);
    const bottomFrontRight = toIsometric(halfWidth, halfDepth, 0);
    const bottomBackRight = toIsometric(halfWidth, -halfDepth, 0);
    const bottomBackLeft = toIsometric(-halfWidth, -halfDepth, 0);

    // 顶部 4 个顶点
    const topFrontLeft = toIsometric(-halfWidth, halfDepth, building.height * scale);
    const topFrontRight = toIsometric(halfWidth, halfDepth, building.height * scale);
    const topBackRight = toIsometric(halfWidth, -halfDepth, building.height * scale);
    const topBackLeft = toIsometric(-halfWidth, -halfDepth, building.height * scale);

    return (
      <g
        key={index}
        style={{
          transform: `translate(${isoX}px, ${isoY}px)`,
          opacity,
        }}
      >
        {/* 顶面 */}
        <polygon
          points={`${topFrontLeft.x},${topFrontLeft.y} ${topFrontRight.x},${topFrontRight.y} ${topBackRight.x},${topBackRight.y} ${topBackLeft.x},${topBackLeft.y}`}
          fill={building.color}
          stroke="rgba(0,0,0,0.1)"
          strokeWidth="1"
        />
        {/* 右侧面 */}
        <polygon
          points={`${topFrontRight.x},${topFrontRight.y} ${topBackRight.x},${topBackRight.y} ${bottomBackRight.x},${bottomBackRight.y} ${bottomFrontRight.x},${bottomFrontRight.y}`}
          fill={building.color}
          opacity={0.8}
          stroke="rgba(0,0,0,0.1)"
          strokeWidth="1"
        />
        {/* 前侧面 */}
        <polygon
          points={`${topFrontLeft.x},${topFrontLeft.y} ${topFrontRight.x},${topFrontRight.y} ${bottomFrontRight.x},${bottomFrontRight.y} ${bottomFrontLeft.x},${bottomFrontLeft.y}`}
          fill={building.color}
          opacity={0.9}
          stroke="rgba(0,0,0,0.1)"
          strokeWidth="1"
        />
        {/* 窗户装饰 */}
        {Array.from({ length: Math.floor(building.height * scale / 30) }).map((_, windowIndex) => (
          <React.Fragment key={windowIndex}>
            <rect
              x={topFrontLeft.x + 5}
              y={topFrontLeft.y - (windowIndex + 1) * 30 + 5}
              width={8}
              height={12}
              fill="rgba(255,255,255,0.3)"
              rx="1"
            />
            <rect
              x={topFrontLeft.x + 20}
              y={topFrontLeft.y - (windowIndex + 1) * 30 + 5}
              width={8}
              height={12}
              fill="rgba(255,255,255,0.3)"
              rx="1"
            />
          </React.Fragment>
        ))}
      </g>
    );
  };

  // 渲染车辆
  const renderVehicle = (vehicle: Vehicle, index: number) => {
    const progress = ((frame * vehicle.speed) % fps) / fps;
    const x = interpolate(progress, [0, 1], [vehicle.startX, vehicle.endX]);
    const y = interpolate(progress, [0, 1], [vehicle.startY, vehicle.endY]);
    const { x: isoX, y: isoY } = toIsometric(x, y, 5);

    return (
      <g key={index} transform={`translate(${isoX}, ${isoY})`}>
        <ellipse cx="0" cy="0" rx="8" ry="4" fill={vehicle.color} />
        <circle cx="-3" cy="-2" r="2" fill="rgba(0,0,0,0.3)" />
        <circle cx="3" cy="-2" r="2" fill="rgba(0,0,0,0.3)" />
      </g>
    );
  };

  // 渲染行人
  const renderPedestrian = (pedestrian: Pedestrian, index: number) => {
    const angle = (frame * pedestrian.speed * 0.1 + index) % (Math.PI * 2);
    const x = Math.cos(angle) * 100 + Math.sin(index) * 50;
    const y = Math.sin(angle) * 100 + Math.cos(index) * 50;
    const { x: isoX, y: isoY } = toIsometric(x, y, 0);

    return (
      <g key={index} transform={`translate(${isoX}, ${isoY})`}>
        <circle cx="0" cy="-6" r="3" fill={pedestrian.color} />
        <ellipse cx="0" cy="0" rx="3" ry="6" fill={pedestrian.color} opacity={0.8} />
      </g>
    );
  };

  // 标题动画
  const titleOpacity = interpolate(entranceProgress, [0.5, 0.8], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });
  const titleY = interpolate(entranceProgress, [0.5, 0.8], [20, 0], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        background: `linear-gradient(135deg, 
          hsl(${bgHue}, 40%, ${bgLightness}%) 0%, 
          hsl(${bgHue + 20}, 50%, ${bgLightness + 10}%) 50%,
          hsl(${bgHue + 40}, 60%, ${bgLightness + 15}%) 100%)`,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* 背景星星（夜晚） */}
      {isNight && (
        <div
          style={{
            position: 'absolute',
            width: '100%',
            height: '100%',
          }}
        >
          {Array.from({ length: 50 }).map((_, i) => {
            const starX = Math.sin(i * 13.7) * width / 2 + width / 2;
            const starY = Math.cos(i * 17.3) * height / 3 + height / 4;
            const twinkle = Math.sin(frame * 0.1 + i) * 0.5 + 0.5;

            return (
              <div
                key={i}
                style={{
                  position: 'absolute',
                  left: starX,
                  top: starY,
                  width: 2,
                  height: 2,
                  backgroundColor: 'white',
                  borderRadius: '50%',
                  opacity: twinkle * 0.8,
                  boxShadow: '0 0 4px white',
                }}
              />
            );
          })}
        </div>
      )}

      {/* 主 SVG 画布 */}
      <svg
        width={width * 0.9}
        height={height * 0.9}
        viewBox="-300 -200 600 400"
        style={{
          transform: `scale(${entranceProgress})`,
          opacity: entranceProgress,
        }}
      >
        {/* 地面网格 */}
        <defs>
          <pattern id="grid" width="100" height="100" patternUnits="userSpaceOnUse">
            <path
              d="M 100 0 L 0 0 0 100"
              fill="none"
              stroke="rgba(255,255,255,0.1)"
              strokeWidth="1"
            />
          </pattern>
        </defs>
        <rect x="-400" y="-300" width="800" height="600" fill="url(#grid)" />

        {/* 中心广场 */}
        <ellipse
          cx={centerPlaza.x}
          cy={centerPlaza.y}
          rx="60"
          ry="30"
          fill="rgba(255,255,255,0.2)"
          stroke="rgba(255,255,255,0.3)"
          strokeWidth="2"
        />

        {/* 建筑物（按深度排序） */}
        {buildings
          .sort((a, b) => a.x + a.y - (b.x + b.y))
          .map((building, index) => renderBuilding(building, index))}

        {/* 车辆 */}
        {vehicles.map((vehicle, index) => renderVehicle(vehicle, index))}

        {/* 行人 */}
        {pedestrians.map((pedestrian, index) => renderPedestrian(pedestrian, index))}

        {/* 装饰粒子 */}
        {Array.from({ length: 30 }).map((_, i) => {
          const angle = (i / 30) * Math.PI * 2 + frame * 0.01;
          const radius = 250 + Math.sin(i * 3) * 50;
          const x = Math.cos(angle) * radius;
          const y = Math.sin(angle) * radius;
          const { x: isoX, y: isoY } = toIsometric(x, y, 0);
          const opacity = Math.sin(frame * 0.05 + i) * 0.3 + 0.5;

          return (
            <circle
              key={i}
              cx={isoX}
              cy={isoY}
              r="2"
              fill={`hsl(${i * 12}, 70%, 70%)`}
              opacity={opacity * entranceProgress}
            />
          );
        })}
      </svg>

      {/* 标题 */}
      <div
        style={{
          position: 'absolute',
          top: 80,
          left: '50%',
          transform: `translate(-50%, ${titleY}px)`,
          opacity: titleOpacity,
          fontFamily: 'system-ui, -apple-system, sans-serif',
          fontWeight: 'bold',
          fontSize: 70,
          color: 'white',
          textShadow: '0 4px 20px rgba(0,0,0,0.3)',
          letterSpacing: '-0.02em',
        }}
      >
        Isometric City
      </div>

      {/* 副标题 */}
      <div
        style={{
          position: 'absolute',
          top: 160,
          left: '50%',
          transform: `translate(-50%, ${titleY}px)`,
          opacity: titleOpacity * 0.9,
          fontFamily: 'system-ui, -apple-system, sans-serif',
          fontSize: 32,
          color: 'rgba(255,255,255,0.9)',
          textShadow: '0 2px 10px rgba(0,0,0,0.2)',
        }}
      >
        等距城市动画 • 2026 创意动画趋势
      </div>

      {/* 信息面板 */}
      <div
        style={{
          position: 'absolute',
          bottom: 15,
          left: 15,
          padding: '20px 30px',
          background: 'rgba(0,0,0,0.3)',
          backdropFilter: 'blur(10px)',
          borderRadius: 12,
          opacity: titleOpacity,
          fontFamily: 'monospace',
          fontSize: 24,
          color: 'white',
          lineHeight: 1.6,
        }}
      >
        <div style={{ fontWeight: 'bold', marginBottom: 8, fontSize: 26 }}>
          🏙️ Isometric City Animation
        </div>
        <div>建筑数量：{buildings.length} 栋</div>
        <div>车辆数量：{vehicles.length} 辆</div>
        <div>行人数量：{pedestrians.length} 人</div>
        <div>昼夜循环：{(bgProgress / 6 * 24).toFixed(0)}:00</div>
        <div>投影角度：30°</div>
      </div>
    </div>
  );
};
